import {
  Box
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { attachmentTableHeaders } from '../../components/project-dashboard/test-case/testCaseTableHead';
import { useNotification } from '../../hooks/NotificationContext';
import { getDisplayUserName, isExecutiveManager } from '../../lib/userUtils';
import { apiRoot } from '../../modules/callApi';
import {
  deleteAttachment,
  getAttachments,
  updateAttachment,
} from '../../modules/entity-details';
import DetailsCard from '../../ui/layout/DetailsCard';
import ConfirmationModal from '../../ui/primitives/ConfirmationModel';
import AttachmentTable from './AttachmentTable';
import FileUpload from './FileUpload';

const Attachments = ({
  entityType,
  entityId,
  showHeader = true,
  refreshData,
  hideTitle = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [order, setOrder] = useState('desc');
  const [uploading, setUploading] = useState(false);
  const [showConfirmationModal, setShowConfirmModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  const { addNotification } = useNotification();
  const { projectId } = useParams();
  const token = localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAttachments(entityType, entityId);
      setData(response.data);
    } catch (error) {
      console.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entityId && entityType) fetchData();
  }, [entityId]);

  const handleFiles = async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);

    setUploading(true);
    try {
      const response = await fetch(
        `${apiRoot}attachments?projectId=${projectId}`,
        {
          method: 'POST',
          body: formData,
          headers: { Authorization: `${token}` },
        },
      );

      if (response.ok) {
        setTimeout(() => {
          addNotification('success', 'Attachment added successfully');
          fetchData();
          refreshData?.();
          setUploading(false);
        }, 500);
      } else {
        addNotification('danger', 'Unable to add attachment');
        setUploading(false);
      }
    } catch (error) {
      console.error('Error adding attachment:', error);
      addNotification('danger', 'Unable to add attachment');
      setUploading(false);
    }
  };

  const handleUpdateEntity = async (id, editingValues) => {
    setLoading(true);
    try {
      await updateAttachment(id, { fileName: editingValues.file_name });
      addNotification('success', 'Updated successfully!');
      fetchData();
    } catch (error) {
      addNotification(
        'danger',
        error?.message || 'Failed to update. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntity = (row) => {
    setEntityToDelete(row.id);
    setShowConfirmModal(true);
  };
  const handleCloseModal = () => setShowConfirmModal(false);

  const handleConfirmDelete = async () => {
    if (!entityToDelete) return;
    try {
      await deleteAttachment(entityToDelete);
      fetchData();
      refreshData?.();
      addNotification('success', 'Deleted successfully!');
    } catch (error) {
      addNotification(
        'danger',
        error?.message || 'Failed to delete. Please try again.',
      );
    } finally {
      setShowConfirmModal(false);
    }
  };

  const tableData = (data ?? []).map((att) => ({
    ...att,
    owner: getDisplayUserName(att?.user),
    file_name: att.file_name.split('.')[0],
  }));

  return (
    <DetailsCard title="Attachments" hidden={hideTitle}>
      {!isExecutiveManager() && (
        <Box mb={2.5}>
          <FileUpload
            enableDragAndDrop
            loading={uploading}
            handleFiles={handleFiles}
          />
        </Box>
      )}

      <AttachmentTable
        data={tableData}
        headerTabs={attachmentTableHeaders}
        isLoading={loading}
        defaultSort="created_at"
        order={order}
        setOrder={setOrder}
        fetchData={fetchData}
        handleUpdateEntity={handleUpdateEntity}
        handleDeleteEntity={handleDeleteEntity}
        editableFields={['file_name']}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirmDelete}
      />
    </DetailsCard>
  );
};

export default Attachments;
